/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, ReactNode} from 'react';
import SelectionsHeader from '../../src/grid/SelectionsHeader';
import {AgGridType, BulkHeader} from './IonGrid';

export interface IonGridHeaderProps extends BulkHeader {
  onBulkEdit?: (assets: any) => void;
  onBulkDelete?: (assets: any) => void;
  onBulkView?: (assets: any) => void;
  onBulkAction?: (value: any) => void;
  onBulkUpdate?: (value: any) => void;
  actionMenuItems?: ReactNode;
  onAction?: (assets: any) => void;
  onBulkAcknowledge?: (events: any) => void;
  hasHeader: boolean;
  selectedRows: Array<any> | [];
  omitCTAs?: boolean;
  gridApi: AgGridType;
  showBulkEdit?: boolean;
  disableBulkEdit?: boolean;
  disableBulkDelete?: boolean;
  customSelectionHeaderButton?: any;
  count: number;
  showDeleteOnSingle?: boolean;
  areTotalRowsSelected?: boolean;
  disableBulkActions?: boolean;
  showDisabledBulkTooltip?: boolean;
}

const MINIMUM_COUNT_TO_SHOW_HEADER = 1;

const COUNT_TO_SHOW_HEADER = 0;

/**
 * @IonGridHeader
 *
 * Defines the IonGridHeader
 *
 * @param {boolean} hasHeader determines if header is visible
 * @param {Function} onBulkEdit function runs when user selects to edit a row
 * @param {Function} onBulkDelete function runs when user selects to delete a row
 * @param {AgGridType} gridApi AgGrid gridApi
 *
 * ```typescript
 *
 *    <IonGridHeader hasHeader={hasHeader}
 *      onBulkEdit={onBulkEdit}
 *      onBulkDelete={onBulkDelete}
 *      gridApi={gridApi} />
 *
 * ```
 */
const IonGridHeader = ({
  onBulkActionsClick,
  hasHeader,
  selectedRows,
  omitCTAs,
  onBulkView,
  onBulkEdit,
  onBulkDelete,
  onBulkAction,
  onBulkUpdate,
  actionMenuItems,
  onAction,
  onBulkAcknowledge,
  customSelectionHeaderButton,
  count,
  showDeleteOnSingle = false,
  disableBulkActions = false,
  showBulkEdit = true,
  disableBulkDelete = false,
  disableBulkEdit = false,
  showDisabledBulkTooltip = false
}: IonGridHeaderProps) => {
  const isMinimumCountSelected = count > (showDeleteOnSingle ? COUNT_TO_SHOW_HEADER : MINIMUM_COUNT_TO_SHOW_HEADER);
  const showBasedOnCount       = isMinimumCountSelected || (count > 0 && (omitCTAs || actionMenuItems));

  if (hasHeader || showBasedOnCount) {
    return (
      <SelectionsHeader selectedRows={selectedRows}
        count={count}
        omitCTAs={omitCTAs}
        onBulkEdit={isMinimumCountSelected && onBulkEdit}
        onBulkActionsClick={onBulkActionsClick}
        onBulkDelete={isMinimumCountSelected && onBulkDelete}
        onBulkAction={isMinimumCountSelected && onBulkAction}
        onBulkUpdate={isMinimumCountSelected && onBulkUpdate}
        onBulkView={isMinimumCountSelected && onBulkView}
        actionMenuItems={actionMenuItems}
        onAction={onAction}
        onBulkAcknowledge={onBulkAcknowledge}
        showBulkEdit={showBulkEdit}
        disableBulkDelete={disableBulkDelete}
        disableBulkEdit={disableBulkEdit}
        disableBulkActions={disableBulkActions}
        showDisabledBulkTooltip={showDisabledBulkTooltip}
        customSelectionHeaderButton={customSelectionHeaderButton} />
    );
  }
};

export default memo(IonGridHeader);
